// i18n
import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { memo, useState, MouseEvent, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import {
  mediaMax,
  fontSize,
  fontWeight,
  spacing,
  isMediaMax,
  zIndex,
} from 'presentations/views/styles';
import {
  white,
  black,
  gray01,
  gray05,
  primary,
  Appearance,
  background,
  gray04,
} from 'presentations/views/styles/color';
import Image from 'presentations/views/components/atoms/Image';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import Popover from 'presentations/views/components/atoms/Popover';
import {
  homePath,
  aboutPath,
  corporateContactPath,
} from 'presentations/routes/path';
import { useRouter } from 'next/router';
import { ThemeContext } from '@emotion/react';
import Logo from 'presentations/views/components/atoms/Logo';

interface Props {}

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  // const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const router = useRouter();
  const activeIndex = useMemo(() => {
    if (router.asPath.indexOf('#corporate') !== -1) {
      return 'corporate';
    } else if (router.asPath.indexOf('#engineering') !== -1) {
      return 'engineering';
    } else if (router.asPath.indexOf('#careers') !== -1) {
      return 'careers';
    }
    return null;
  }, [router.asPath]);

  const contactPath = useMemo(() => {
    return corporateContactPath(router.locale);
  }, [router.locale]);

  // const onClickMenu = (e: MouseEvent<HTMLElement>) => {
  //   e.preventDefault();
  //   setMenuAnchor(menuAnchor !== null ? null : e.currentTarget);
  // };

  // const onCloseMenu = () => {
  //   setMenuAnchor(null);
  // };

  const isMobile = isMediaMax('tablet');

  // const menuList = [
  //   {
  //     label: t('Engineering'),
  //     undefined,
  //     icon: <ListIcon src="pages/top/icon-bar.svg" alt="" />,
  //     link: engineeringListPath(),
  //   },
  //   {
  //     label: t('Contact'),
  //     undefined,
  //     icon: <ListIcon src="pages/top/icon-bar.svg" alt="" />,
  //     link: corporateContactPath(router.locale),
  //   },
  // ];
  const p = router.asPath;
  const context: any = useContext(ThemeContext);
  const isDarkMode = context.appearance === Appearance.Dark;

  return (
    <Container>
      <LeftBox>
        {!isMobile && (
          <Link href={homePath()}>
            {isDarkMode ? (
              <ServiceImage
                src={'logo/logo_jicoo-whB.svg'}
                alt={translate('siteName')}
                width={130}
                height={34}
              />
            ) : (
              <ServiceLogo width={130} height={34} />
            )}
          </Link>
        )}
        <Navigation>
          <NavigationItem>
            <NavigationLink href={aboutPath()} isCurrent={activeIndex === null}>
              {t('About Us')}
            </NavigationLink>
          </NavigationItem>
          <NavigationItem>
            <NavigationLink
              href="#corporate"
              isCurrent={activeIndex === 'corporate'}
            >
              {t('Corporate')}
            </NavigationLink>
          </NavigationItem>
          <NavigationItem>
            <NavigationLink
              href="#careers"
              isCurrent={activeIndex === 'careers'}
            >
              {t('Career')}
            </NavigationLink>
          </NavigationItem>
          <NavigationItem>
            <NavigationLink
              href="#engineering"
              isCurrent={activeIndex === 'engineering'}
            >
              {t('Engineering')}
            </NavigationLink>
          </NavigationItem>
          <NavigationItem>
            <NavigationLink
              href={contactPath}
              isCurrent={p.startsWith(contactPath)}
            >
              {t('Contact')}
            </NavigationLink>
          </NavigationItem>
          {/* <NavigationItem>
            <NavigationLink
              onClick={onClickMenu}
              isCurrent={p.startsWith(engineeringListPath())}
            >
              {t('Menu')}
              <ExpandIcon
                name={menuAnchor ? 'expand_less' : 'expand_more'}
                size={fontSize(20)}
              />
            </NavigationLink>
          </NavigationItem> */}
          {/* {menuAnchor && (
            <Popover
              onClose={onCloseMenu}
              anchor={menuAnchor}
              anchorReference="anchorEl"
            >
              <MenuItemList>
                {menuList.map((menu) => (
                  <MenuItem key={menu.label} href={menu.link}>
                    {menu.icon}
                    <MenuItemName>{menu.label}</MenuItemName>
                  </MenuItem>
                ))}
              </MenuItemList>
            </Popover>
          )} */}
        </Navigation>
      </LeftBox>
      <BrandContainer>
        {isMobile && (
          <Link href={homePath()}>
            {isDarkMode ? (
              <ServiceImage
                src={'logo/logo_jicoo-whB.svg'}
                alt={translate('siteName')}
                width={130}
                height={34}
              />
            ) : (
              <ServiceLogo width={130} height={34} />
            )}
          </Link>
        )}
      </BrandContainer>
      <RightBox>
        <MenuList></MenuList>
      </RightBox>
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  align-items: center;
  height: 80px;
  position: sticky;
  top: 0;
  background-color: ${background};
  border-bottom: 1px solid ${gray04};
  -webkit-app-region: drag;
  z-index: ${zIndex(100)};
  ${mediaMax.tablet} {
    height: 54px;
    /* z-index: ${zIndex(1400)}; */
    justify-content: space-between;
  }
`;
const LeftBox = styled.div`
  margin-right: auto;
  margin-left: ${spacing(24)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    margin-left: ${spacing(16)};
    min-width: 80px;
    margin-right: initial;
  }
  ${mediaMax.xs} {
    width: 34px;
    overflow: hidden;
  }
`;
const Navigation = styled.ul`
  margin-left: ${spacing(48)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    display: none;
  }
`;
const NavigationItem = styled.li`
  margin-right: ${spacing(32)};
  :last-of-type {
    margin-right: 0;
  }
`;
const NavigationLink = styled(Link)<{ isCurrent?: boolean }>`
  color: ${(props) => (props.isCurrent ? primary : gray01)};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(14)};
  display: flex;
  align-items: center;
`;
const ServiceImage = styled(Image)`
  width: 130px;

  ${mediaMax.tablet} {
    width: 89px;
    height: auto;
  }
`;
const RightBox = styled.div`
  margin-right: ${spacing(24)};
  ${mediaMax.tablet} {
    margin-right: ${spacing(8)};
    min-width: 80px;
  }
  ${mediaMax.xs} {
    margin-right: ${spacing(3)};
  }
`;
const MenuList = styled.ul`
  display: flex;
  align-items: center;
`;

const ListIcon = styled(Image)`
  font-size: ${fontSize(24)} !important;
  color: ${gray01};
  padding-left: 6px;
  width: 16px;
`;
const BrandContainer = styled.div`
  flex: 1;
  text-align: center;
`;
const ExpandIcon = styled(Icon)`
  min-width: 20px;
  min-height: 20px;
`;
const MenuItemList = styled.div`
  padding: ${spacing(16)};
`;
const MenuItem = styled(Link)`
  display: flex;
  align-items: center;
  font-size: ${fontSize(13)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(16)};
  :last-of-type {
    margin-bottom: 0;
  }
  color: ${gray01};
  :hover {
    color: ${primary};
  }
`;
const MenuItemName = styled.div`
  margin-left: ${spacing(12)};
  color: ${gray01};
`;
const ServiceLogo = styled(Logo)`
  ${mediaMax.tablet} {
    width: 89px;
  }
`;

export default Component;
